import React, { useState, useEffect, useRef } from 'react'

import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import update from 'immutability-helper'
import { Tooltip } from '@mui/material'
import allLocales from '@fullcalendar/core/locales-all'
import i18next from 'i18next'

import AppLayout from '../../../components/Layouts/AppLayout'
import { useStateContext } from '../../../context/ContextProvider'
import axios from '../../../lib/axios'
import '../../../CollectionCalendar.css'
import Loading from '../../../components/Loading'
import Navigation from '../../../components/sales_purchase_orders/Navigation'
import { isValid } from '../../../helpers/helper'
import { useAuth } from '../../../hooks/auth'
import { includes } from 'lodash'
import { buildUrl } from '../../../helpers/apiHelper'
import dayjs from 'dayjs'

const PurchaseOrderDailyBoardCalendar = () => {

    const { user } = useAuth({ middleware: 'guest ' })

    const { t } = useTranslation()
    const [orders, setOrders] = useState([])
    const { config, choosesite, setChoosesite, pusher } = useStateContext()
    const calendarRef = useRef(null)
    const navigate = useNavigate()
    const location = useLocation()
    const [isLoading, setIsLoading] = useState(false)

    const [events, setEvents] = useState([])

    useEffect(() => {
        // Unsubscribe on unmount
        return (() => {
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-deleted-site-${localStorage.getItem('site')}`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-updated-site-${localStorage.getItem('site')}`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-vendoruser-updated`)
        })
    }, [])

    useEffect(() => {
        if (isValid(choosesite)) {
            const channelPurchaseOrderDeleted = pusher.subscribe(`${localStorage.getItem('client_id')}-purchaseorder-deleted-site-${choosesite}`)
            const channelPurchaseOrderUpdated = pusher.subscribe(`${localStorage.getItem('client_id')}-purchaseorder-updated-site-${choosesite}`)
            const channelVendorUserUpdated = pusher.subscribe(`${localStorage.getItem('client_id')}-vendoruser-updated`)

            channelPurchaseOrderDeleted.bind(`${localStorage.getItem('client_id')}-purchaseorder-deleted-event-site-${choosesite}`, data => {
                setOrders((prev) => {
                    const item = prev.find((i) => i.id === data.id)
                    const exItem = prev.filter((i) => i.id !== item.id)
                    return exItem
                })
            })

            channelPurchaseOrderUpdated.bind(`${localStorage.getItem('client_id')}-purchaseorder-updated-event-site-${choosesite}`, data => {
                getPurchaseOrder(data.id, 'updated')
            })

            channelVendorUserUpdated.bind(`${localStorage.getItem('client_id')}-vendoruser-updated-event`, data => {
                if(data.id == user?.id) {
                    getOrders(choosesite)
                }
            })
        }

        return (() => {
            if(isValid(choosesite)) {
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-deleted-site-${choosesite}`)
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-updated-site-${choosesite}`)
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-vendoruser-updated`)
            }
        })
    }, [choosesite, user])

    useEffect(() => {
        setEvents([])
        orders.map((i) => {
            let newEvent = {
                "order_id": i.broker_transaction?.id ?? i.id,
                "title": i.po_number,
                "date": i.requested_collection_date + 'T' + i.requested_collection_time_time,
                "requested_collection_date": i.requested_collection_date,
                "customer_name": i.vendor_name,
                "ship_address": i.purchase_address_name
            }

            setEvents((prev) => [...prev, newEvent])
        })
    }, [orders])

    {/* Functions */}

    const getOrders = async (
        siteId,
        type = 'broker',
        timespan = 'dayGridMonth',
        day = null,
        month = null,
        year = null
    ) => {
        setEvents([]);
        setIsLoading(true);

        const url = buildUrl(
            `/api/frontend-calendar/service-provider-user/purchase`,
            {
                where: {
                    site_id: siteId,
                    type: type,
                    timespan: timespan,
                    day: day,
                    month: month,
                    year: year
                }
            }
        );

        await axios.get(url, config)
            .then(({ data }) => setOrders(data.data))
            .finally(() => setIsLoading(false));
    }

    const getPurchaseOrder = async (id, state) => {
        setIsLoading(true)

        await axios
            .get(`/api/purchase-orders/${id}`, config)
            .then(res => {
                const data = res.data
                if (state === 'created') {
                    setOrders((prev) => [...prev, data])
                }

                if (state === 'updated') {
                    setOrders((prev) => {

                        const index = prev?.findIndex((i) => i.id === id)
                        if (index < 0) {
                            const items = [...prev, data]
                            return items
                        } else {
                            if (includes(['open', 'reopened', 'completed', 'invoiced', 'rejected'], data.po_status)) {
                                return update(prev, { $splice: [[index, 1]] })
                            } else {
                                return update(prev,
                                    { [index]: { $set: data } }
                                )
                            }
                        }
                    })
                }
            })
            .catch(({ response }) => {})
            .finally(() => setIsLoading(false))
    }

    const handleEventClick = (eventInfo) => {
        localStorage.setItem('initialDatePurchase', eventInfo.event.extendedProps.requested_collection_date)
        navigate(`/purchase-orders/${eventInfo.event.extendedProps.order_id}`, { state: { route: '/purchase-order-calendar', prevPathname: location.pathname } });
    }

    const handleDatesSet = (arg) => {
        const calendarApi = calendarRef.current?.getApi();
        if(!calendarApi) {
            let initialDate = null;
            try {
                initialDate = dayjs(localStorage.getItem('initialDatePurchase'));
            } catch (e) {
                initialDate = dayjs();
            }

            getOrders(
                localStorage.getItem('site'),
                'broker',
                'dayGridMonth',
                initialDate.isValid() ? initialDate.date() : null,
                initialDate.isValid() ? (initialDate.month() + 1) : null,
                initialDate.isValid() ? initialDate.year() : null
            );

            return;
        };

        const currentDate = dayjs(calendarApi.getDate());

        const day = currentDate.date();
        const month = currentDate.month() + 1;
        const year = currentDate.year();
        const timespan = arg?.view?.type;

        getOrders(
            localStorage.getItem('site'),
            'broker',
            timespan,
            day,
            month,
            year
        );
    }

    return (
        <>
            {isLoading ? <Loading /> : null}
            <AppLayout>
                <Navigation route='purchase-orders' type='calendar' user={user} />
                <div className='p-5 w-full'>
                    <div className='p-2 bg-white mb-2 rounded-md collection'>
                        <FullCalendar
                            locales={allLocales}
                            locale={i18next?.resolvedLanguage ?? 'en'}
                            plugins={[dayGridPlugin, timeGridPlugin]}
                            ref={calendarRef}
                            headerToolbar={{
                                left: 'prev,next today',
                                center: 'title',
                                right: 'dayGridMonth,timeGridWeek,timeGridDay'
                            }}
                            buttonText={{
                                today: t('today'),
                                month: t('month'),
                                week: t('week'),
                                day: t('day')
                            }}
                            customButtons={{
                                today: {
                                    text: t('today'),
                                    click: () => {
                                        localStorage.removeItem('initialDatePurchase')
                                        let calendarApi = calendarRef.current.getApi()
                                        calendarApi.today()
                                    }
                                }
                            }}
                            initialView='dayGridMonth'
                            events={events}
                            eventContent={renderEventContent}
                            dayMaxEvents={true}
                            eventMaxStack={1}
                            expandRows={true}
                            eventColor='#ffffff'
                            initialDate={localStorage.getItem('initialDatePurchase')}
                            eventClick={handleEventClick}
                            datesSet={handleDatesSet}
                        />
                    </div>
                </div>
            </AppLayout>
        </>
    )
}

export default PurchaseOrderDailyBoardCalendar

function renderEventContent(eventInfo) {

    return (
        <Tooltip disableInteractive placement='bottom' title={
            <div className='flex flex-col'>
                <p className='text-[16px] pb-3'>{eventInfo.event.title}</p>
                <i className='text-[14px]'>{eventInfo.event.extendedProps.customer_name}</i>
                <i className='text-[14px]'>{eventInfo.event.extendedProps.ship_address}</i>
            </div>
        }>
            <button className='w-full'>
                <div className='p-1 flex flex-col text-left bg-[#b7472a] text-white rounded-sm'>
                    <p className='text-xl bold p-1 roboto'>{eventInfo.event.title}</p>
                    <i>{eventInfo.event.extendedProps.customer_name}</i>
                    <i>{eventInfo.event.extendedProps.ship_address}</i>
                </div>
            </button>
        </Tooltip>
    )
}
